import type { Price } from '@sweethome/types';

export function formatPrice(
  amount: Price | number | { amount?: number; currency_code?: string },
  locale: 'fr' | 'en' = 'fr',
  currency?: string
): string {
  const numericAmount =
    typeof amount === 'number'
      ? amount
      : typeof amount === 'object' && amount !== null
        ? (amount.amount ?? 0)
        : 0;
  const currencyCode =
    currency ||
    (typeof amount === 'object' &&
    amount !== null &&
    'currency_code' in amount &&
    amount.currency_code
      ? String(amount.currency_code).toUpperCase()
      : 'EUR');

  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency: currencyCode,
  }).format(numericAmount);
}

export function parsePrice(priceString: string): number | null {
  // Remove spaces
  let normalized = priceString.replace(/\s/g, '');
  // If we see both dot and comma, assume the last one is the decimal separator
  if (normalized.includes('.') && normalized.includes(',')) {
    if (normalized.indexOf(',') > normalized.indexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (normalized.includes(',')) {
    // only comma, assume decimal
    normalized = normalized.replace(',', '.');
  }

  const match = normalized.match(/(\d+(?:\.\d+)?)/);
  if (!match || !match[1]) return null;

  return parseFloat(match[1]);
}

/**
 * Convert a Medusa price expressed in MINOR units (cents) into MAJOR units.
 *
 * Medusa stores all monetary values in minor units at the API boundary
 * (e.g. 99900 = 999.00 EUR). The storefront, however, formats prices via
 * `Intl.NumberFormat({ style: "currency" })` which expects MAJOR units.
 * Forgetting to convert causes a 100x display error (e.g. "99 900,00 €"
 * instead of "999,00 €").
 *
 * Use this helper at the API → UI boundary whenever you read a price
 * field from Medusa (`calculated_price.calculated_amount`,
 * `variant.prices[].amount`, etc.) and intend to pass it to
 * `formatPrice` / `createPrice`.
 *
 * The input type is `number | null | undefined` because most Medusa
 * wire fields are optional (`calculated_price.calculated_amount` is
 * `number | undefined`, `original_amount` is `number | null | undefined`).
 * `null` and `undefined` pass through as `0` so call sites that read
 * `convertMinorUnitsToMajor(value)` and then immediately call
 * `formatPrice(value)` do not need a separate null guard.
 *
 * @param minorUnits - price in minor units (cents). 0 is a valid input.
 *                    `null` / `undefined` map to `0`.
 * @returns price in major units (euros for EUR). Always a finite number.
 */
export function convertMinorUnitsToMajor(minorUnits: number | null | undefined): number {
  if (minorUnits == null || !Number.isFinite(minorUnits)) return 0;
  return minorUnits / 100;
}

/**
 * Money-field names whose values Medusa returns in MINOR units (cents).
 *
 * The Medusa Store/Admin API returns most monetary fields as minor units
 * (e.g. `subtotal: 99900` for €999.00). The boundary transformer below
 * uses this set to convert those fields to MAJOR units (`999.00`) so the
 * UI can pass them straight to `Intl.NumberFormat` without re-thinking
 * the unit convention at every call site.
 */
export const MINOR_UNIT_MONEY_FIELDS: ReadonlySet<string> = new Set([
  // Cart & Order totals (raw API shape)
  'subtotal',
  'total',
  'original_total',
  'discount_total',
  'shipping_total',
  'tax_total',
  'gift_card_total',
  'gift_card_amount',
  // Cart & Order line items
  'unit_price',
  'original_unit_price',
  // Refunds / payments
  'refund_amount',
  'refunded_total',
  'paid_total',
  'refundable_amount',
  'captured_amount',
  'authorized_amount',
  // Calculated price (e.g. `calculated_price.calculated_amount`)
  'calculated_amount',
  'original_amount',
  // Shipping method prices (raw API shape)
  'price',
  'original_price',
  'minimum_price',
  'maximum_price',
  // Variant price entries — raw API shape
  'amount',
]);

/**
 * Fields whose value is already in MAJOR units — the transformer must
 * leave these alone.
 */
export const MAJOR_UNIT_MONEY_FIELDS: ReadonlySet<string> = new Set([]);

const MAX_TRANSFORM_DEPTH = 16;

/**
 * Recursively walk an arbitrary payload and convert every numeric value
 * whose key is in `MINOR_UNIT_MONEY_FIELDS` from MINOR → MAJOR units.
 *
 * This is the **boundary normalization** strategy: applied once at the
 * SDK/fetcher edge, every downstream consumer can treat prices as major
 * units and call `formatPrice(amount)` directly.
 */
export function transformMoneyFields<T>(payload: T): T {
  return _transformMoneyFields(payload, 0) as T;
}

function _transformMoneyFields(payload: unknown, depth: number): unknown {
  if (depth > MAX_TRANSFORM_DEPTH) return payload;
  if (payload === null || payload === undefined) return payload;

  // Primitives other than plain objects / arrays pass through unchanged.
  if (typeof payload !== 'object') return payload;
  if (payload instanceof Date) return payload;

  if (Array.isArray(payload)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: any[] = new Array(payload.length);
    let mutated = false;
    for (let i = 0; i < payload.length; i++) {
      const next = _transformMoneyFields(payload[i], depth + 1);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      out[i] = next as any;
      if (next !== payload[i]) mutated = true;
    }
    return mutated ? out : payload;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const source = payload as Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const out: Record<string, any> = {};
  let mutated = false;

  for (const key of Object.keys(source)) {
    const value = source[key];

    if (MINOR_UNIT_MONEY_FIELDS.has(key) && typeof value === 'number' && Number.isFinite(value)) {
      out[key] = convertMinorUnitsToMajor(value);
      mutated = true;
      continue;
    }

    if (MAJOR_UNIT_MONEY_FIELDS.has(key)) {
      out[key] = value;
      mutated = true;
      continue;
    }

    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      const next = _transformMoneyFields(value, depth + 1);
      out[key] = next;
      if (next !== value) mutated = true;
      continue;
    }

    out[key] = value;
  }

  return mutated ? out : payload;
}
